/**
 * 用户活跃状态守卫模块
 * 功能：用户登录后无任何操作连续 120 分钟自动退出并重定向至登录界面
 * 特性：
 * 1. 跨多标签页状态同步（基于 localStorage + storage 事件）
 * 2. 息屏/锁屏唤醒校验（基于 visibilitychange + focus 事件）
 * 3. 高性能节流写入，零卡顿
 * 4. 健壮的防并发退出保护与安全清理
 */
import { get_api_data } from "@/utils/ajax";

// 超时阈值：120 分钟（毫秒）
const TIMEOUT_MS = 120 * 60 * 1000;

// localStorage 存储键名
const STORAGE_KEY = "material_last_active_time";

// 检测轮询间隔：15 秒
const CHECK_INTERVAL_MS = 15 * 1000;

// 用户事件节流间隔：1 秒
const EVENT_THROTTLE_MS = 1000;

class ActivityTracker {
    constructor() {
        this.timer = null;
        this.lastThrottleTime = 0;
        this.isLoggingOut = false;
        this.isRunning = false;

        // 绑定上下文
        this.handleUserActivity = this.handleUserActivity.bind(this);
        this.handleStorageChange = this.handleStorageChange.bind(this);
        this.handleVisibilityOrFocus = this.handleVisibilityOrFocus.bind(this);
        this.checkInactivity = this.checkInactivity.bind(this);
    }

    /**
     * 启动活跃守卫
     */
    start() {
        if (this.isRunning) return;
        this.isRunning = true;
        this.isLoggingOut = false;

        // 1. 初始化当前活跃时间戳（若本地无记录则写入当前时间）
        const existingTime = this.getLastActiveTime();
        const now = Date.now();
        if (!existingTime || now - existingTime >= TIMEOUT_MS) {
            this.setLastActiveTime(now);
        }

        // 2. 监听用户活跃事件
        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
        events.forEach((eventName) => {
            window.addEventListener(eventName, this.handleUserActivity, { passive: true });
        });

        // 3. 监听多标签页同步
        window.addEventListener("storage", this.handleStorageChange);

        // 4. 监听屏幕唤醒与标签页切回
        document.addEventListener("visibilitychange", this.handleVisibilityOrFocus);
        window.addEventListener("focus", this.handleVisibilityOrFocus);

        // 5. 启动周期性轮询
        this.timer = setInterval(this.checkInactivity, CHECK_INTERVAL_MS);
    }

    /**
     * 停止活跃守卫（组件销毁时调用）
     */
    stop() {
        if (!this.isRunning) return;
        this.isRunning = false;

        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }

        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
        events.forEach((eventName) => {
            window.removeEventListener(eventName, this.handleUserActivity);
        });

        window.removeEventListener("storage", this.handleStorageChange);
        document.removeEventListener("visibilitychange", this.handleVisibilityOrFocus);
        window.removeEventListener("focus", this.handleVisibilityOrFocus);
    }

    /**
     * 读取本地存储的最后活跃时间戳
     */
    getLastActiveTime() {
        try {
            const val = localStorage.getItem(STORAGE_KEY);
            return val ? parseInt(val, 10) : 0;
        } catch (e) {
            return 0;
        }
    }

    /**
     * 写入最后活跃时间戳
     */
    setLastActiveTime(timestamp) {
        try {
            localStorage.setItem(STORAGE_KEY, String(timestamp));
        } catch (e) {}
    }

    /**
     * 响应用户活跃事件（节流 1000ms 减少 localStorage 写入频度）
     */
    handleUserActivity() {
        if (this.isLoggingOut) return;
        const now = Date.now();
        if (now - this.lastThrottleTime > EVENT_THROTTLE_MS) {
            this.lastThrottleTime = now;
            this.setLastActiveTime(now);
        }
    }

    /**
     * 跨标签页活跃时间同步（当其他 Tab 操作时，本 Tab 同步更新）
     */
    handleStorageChange(event) {
        if (event.key === STORAGE_KEY && event.newValue) {
            const remoteTime = parseInt(event.newValue, 10);
            if (remoteTime > this.lastThrottleTime) {
                this.lastThrottleTime = remoteTime;
            }
        }
    }

    /**
     * 息屏唤醒或标签页恢复激活时的即时检测
     */
    handleVisibilityOrFocus() {
        if (this.isLoggingOut) return;
        if (document.visibilityState === "visible") {
            this.checkInactivity();
        }
    }

    /**
     * 核心超时检测
     */
    checkInactivity() {
        if (this.isLoggingOut) return;

        const lastActive = this.getLastActiveTime();
        if (!lastActive) return;

        const now = Date.now();
        const inactiveDuration = now - lastActive;

        if (inactiveDuration >= TIMEOUT_MS) {
            this.triggerLogout();
        }
    }

    /**
     * 执行注销并跳转至登录页
     */
    async triggerLogout() {
        if (this.isLoggingOut) return;
        this.isLoggingOut = true;

        this.stop();

        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {}

        // 发送注销请求至后端，销毁服务端 Session
        try {
            await get_api_data("POST", "logout");
        } catch (e) {
            // 即使网络异常也必须保证前端跳转
        }

        // 重定向至登录界面并附带超时标记
        window.location.href = "/login?timeout=1";
    }
}

export default new ActivityTracker();

